import { useState, useMemo, useRef, useEffect } from "react"
import { X, Plus, Clock, ChevronRight } from "lucide-react"
import {
  Button,
  Input,
  NumberInput,
  Textarea,
  Select,
} from "../../ui"
import { RULES, validateFields, hasErrors } from "../../fieldRules"
import { RequirementInputs, LOCKED_REASON, cleanTerms } from "./RequirementsSection"

const QUICK_DEADLINES = [
  { label: "+3 ngày", days: 3 },
  { label: "+7 ngày", days: 7 },
  { label: "+14 ngày", days: 14 },
]

const SUGGESTED_DOCS = [
  "Quotation",
  "Spec sheet",
  "Company profile",
  "Business license",
]

function addDays(n) {
  const d = new Date()
  d.setDate(d.getDate() + n)
  // local datetime for input[type=datetime-local]
  const pad = (x) => String(x).padStart(2, "0")
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function todayStr() {
  const d = new Date()
  const pad = (x) => String(x).padStart(2, "0")
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`
}

// ── Supplier chip ──────────────────────────────────────────────────────────
function SupplierChip({ supplier, onRemove }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "4px 10px",
        background: "var(--pc-brand-soft)",
        border: "1px solid #c7d2fe",
        borderRadius: "var(--pc-r-pill)",
        fontSize: 12.5,
        fontWeight: 600,
        color: "var(--pc-brand)",
      }}
    >
      {supplier.name}
      <button
        type="button"
        onClick={() => onRemove(supplier.id)}
        style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: "inherit", display: "flex" }}
        aria-label={`Remove ${supplier.name}`}
      >
        <X size={13} />
      </button>
    </span>
  )
}

// ── Supplier search dropdown ───────────────────────────────────────────────
function SupplierSearch({ suppliers, selected, activeIds, onSelect }) {
  const [q, setQ] = useState("")
  const [open, setOpen] = useState(false)

  const filtered = useMemo(() => {
    const lq = q.toLowerCase()
    return (suppliers || []).filter(
      (s) =>
        (s.name?.toLowerCase().includes(lq) ||
          s.taxCode?.toLowerCase().includes(lq) ||
          s.email?.toLowerCase().includes(lq))
    )
  }, [suppliers, q])

  const selectedSet = new Set(selected.map((s) => String(s.id)))

  return (
    <div style={{ position: "relative" }}>
      <input
        className="pc-control"
        placeholder="Tìm nhà cung cấp theo tên, MST, email…"
        value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true) }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 180)}
        aria-label="Tìm nhà cung cấp"
      />
      {open && (
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 4px)",
            left: 0,
            right: 0,
            background: "var(--pc-surface)",
            border: "1px solid var(--pc-border)",
            borderRadius: "var(--pc-r-md)",
            boxShadow: "0 4px 20px rgba(15,23,42,.12)",
            zIndex: 200,
            maxHeight: 260,
            overflowY: "auto",
          }}
        >
          {filtered.length === 0 && (
            <div style={{ padding: "12px 14px", fontSize: 13, color: "var(--pc-muted)" }}>
              Không tìm thấy nhà cung cấp
            </div>
          )}
          {filtered.map((s) => {
            const isSelected = selectedSet.has(String(s.id))
            const isActive = activeIds?.has(String(s.id))
            return (
              <button
                key={s.id}
                type="button"
                title={isActive ? "Đã có yêu cầu đang chờ" : undefined}
                disabled={isActive}
                style={{
                  display: "flex",
                  alignItems: "center",
                  width: "100%",
                  padding: "10px 14px",
                  background: isSelected ? "var(--pc-brand-soft)" : "transparent",
                  border: "none",
                  cursor: isActive ? "not-allowed" : "pointer",
                  opacity: isActive ? 0.55 : 1,
                  gap: 10,
                  textAlign: "left",
                  borderBottom: "1px solid var(--pc-border)",
                }}
                onClick={() => {
                  if (!isActive) onSelect(s)
                  setQ("")
                }}
              >
                <span style={{ flex: 1, minWidth: 0 }}>
                  <span style={{ fontSize: 13.5, fontWeight: 600, color: "var(--pc-ink)", display: "block" }}>
                    {s.name}
                  </span>
                  <span style={{ fontSize: 11.5, color: "var(--pc-muted)" }}>
                    {s.taxCode}
                    {s.email ? ` · ${s.email}` : " · (Chưa có email)"}
                  </span>
                </span>
                {isSelected && (
                  <span style={{ fontSize: 11, color: "var(--pc-brand)", fontWeight: 700 }}>✓ Đã chọn</span>
                )}
                {isActive && (
                  <span style={{ fontSize: 11, color: "var(--pc-warn)", fontWeight: 600 }}>Đang chờ</span>
                )}
              </button>
            )
          })}
        </div>
      )}
      {/* Warnings for suppliers without email */}
      {selected.filter((s) => !s.email).map((s) => (
        <div key={s.id} style={{ marginTop: 4, fontSize: 12, color: "var(--pc-warn)" }}>
          ⚠ {s.name}: Chưa có email – chỉ gửi được bằng link
        </div>
      ))}
    </div>
  )
}

// ── Main drawer ────────────────────────────────────────────────────────────
// Order lines come keyed by proposalLineKey (catalog item or off-catalog line).
const lineKeyOf = (it) => it.key ?? (it.itemId || it.id)

// `initialRequirements` joined by "\n" (a requirement is one line) — a stable effect dep.
const requirementRows = (key) => (key ? key.split("\n") : [""])

const matrixCell = { padding: "8px 10px", borderBottom: "1px solid var(--pc-border)", textAlign: "left" }
const matrixHead = { ...matrixCell, background: "var(--pc-surface-2)", fontWeight: 600, whiteSpace: "nowrap" }

export default function CreateProposalDrawer({
  open,
  onClose,
  onSubmit,
  suppliers = [],
  orderItems = [],
  itemsLoading = false,
  defaultTitle = "",
  defaultDeliveryLocation = "",
  currentUser = {},
  existingActiveSupplierIds = new Set(),
  initialRequirements = [],
  requirementsLocked = false,
  isSubmitting = false,
}) {
  const [selectedSuppliers, setSelectedSuppliers] = useState([])
  const [showNewSupplier, setShowNewSupplier] = useState(false)
  const [newSupplierDraft, setNewSupplierDraft] = useState({ name: "", taxCode: "", email: "" })
  const [newSupplierErrors, setNewSupplierErrors] = useState({})
  const setNewSupplierField = (k, v) => {
    setNewSupplierDraft((d) => ({ ...d, [k]: v }))
    setNewSupplierErrors((p) => ({ ...p, [k]: null }))
  }
  // Not created via a separate API call — queued here and sent as part of
  // the same submit (`newSuppliers`), so the backend creates them as
  // permanent Supplier rows atomically with the RFQ itself (409 on a
  // duplicate taxCode). A standalone "create supplier" endpoint still exists
  // elsewhere in the app; this drawer just doesn't call it.
  const [newSupplierDrafts, setNewSupplierDrafts] = useState([])
  const [form, setForm] = useState({
    title: defaultTitle,
    deadline: addDays(7),
    note: "",
    deliveryLocation: defaultDeliveryLocation,
    expectedDeliveryDate: "",
    requirePin: true,
    deliveryFee: "",
  })
  const [selectedItemIds, setSelectedItemIds] = useState(new Set(orderItems.map(lineKeyOf)))
  // `orderItems` can arrive asynchronously after this drawer is already open
  // (CreatePurchaseContainer's PO-scoped RFQ: items load from a separate
  // query keyed off a draft PO id that may not exist yet at mount) — the
  // `useState` initializer above only runs once, so pre-select everything
  // the first time a non-empty list actually shows up, without stomping on
  // the user's own later toggles.
  const seededItemsRef = useRef(orderItems.length > 0)
  useEffect(() => {
    if (seededItemsRef.current || orderItems.length === 0) return
    setSelectedItemIds(new Set(orderItems.map(lineKeyOf)))
    seededItemsRef.current = true
  }, [orderItems])
  // B4: "supplierId|lineKey" pairs NOT invited. Empty = everyone quotes every line.
  const [excludedPairs, setExcludedPairs] = useState(new Set())
  // The OR/PO's requirement set is shared by every vendor, so the drawer starts
  // from it on each open — inviting more vendors resends it unchanged.
  const initialRequirementsKey = initialRequirements.join("\n")
  const [requirements, setRequirements] = useState(() => requirementRows(initialRequirementsKey))
  useEffect(() => {
    if (open) setRequirements(requirementRows(initialRequirementsKey))
  }, [open, initialRequirementsKey])
  const [requestedDocs, setRequestedDocs] = useState(
    SUGGESTED_DOCS.slice(0, 2).map((name) => ({ name, required: true }))
  )
  const [newDocName, setNewDocName] = useState("")
  const [contact, setContact] = useState({
    name: currentUser?.name || "",
    email: currentUser?.email || "",
    phone: currentUser?.phone || "",
  })

  const setField = (k, v) => setForm((p) => ({ ...p, [k]: v }))

  const handleSelectSupplier = (s) => {
    setSelectedSuppliers((prev) =>
      prev.find((x) => String(x.id) === String(s.id))
        ? prev.filter((x) => String(x.id) !== String(s.id))
        : [...prev, s]
    )
  }

  const handleRemoveSupplier = (id) => {
    setSelectedSuppliers((prev) => prev.filter((s) => String(s.id) !== String(id)))
  }

  const handleQueueNewSupplier = () => {
    if (!newSupplierDraft.name.trim() || !newSupplierDraft.taxCode.trim()) return
    const errs = validateFields(newSupplierDraft, RULES.supplier)
    setNewSupplierErrors(errs)
    if (hasErrors(errs)) return
    setNewSupplierDrafts((prev) => [...prev, newSupplierDraft])
    setShowNewSupplier(false)
    setNewSupplierDraft({ name: "", taxCode: "", email: "" })
  }

  const handleRemoveNewSupplierDraft = (index) => {
    setNewSupplierDrafts((prev) => prev.filter((_, i) => i !== index))
  }

  const toggleItem = (id) => {
    setSelectedItemIds((prev) => {
      const next = new Set(prev)
      next.has(id) ? next.delete(id) : next.add(id)
      return next
    })
  }


  const addDoc = () => {
    if (!newDocName.trim()) return
    setRequestedDocs((p) => [...p, { name: newDocName.trim(), required: false }])
    setNewDocName("")
  }
  const removeDoc = (i) => setRequestedDocs((p) => p.filter((_, j) => j !== i))
  const toggleDocRequired = (i) =>
    setRequestedDocs((p) => p.map((d, j) => (j === i ? { ...d, required: !d.required } : d)))

  const selectedItems = orderItems.filter((it) => selectedItemIds.has(lineKeyOf(it)))
  const deliveryFeeValid = form.deliveryFee === "" || Number(form.deliveryFee) >= 0

  const showLineMatrix = selectedSuppliers.length + newSupplierDrafts.length >= 2 && selectedItems.length > 0
  const pairKey = (supplierId, lineKey) => `${supplierId}|${lineKey}`
  // Hidden matrix (single supplier) → leftover exclusions must not apply.
  const isInvited = (supplierId, lineKey) => !showLineMatrix || !excludedPairs.has(pairKey(supplierId, lineKey))
  const togglePair = (supplierId, lineKey) =>
    setExcludedPairs((prev) => {
      const next = new Set(prev)
      const k = pairKey(supplierId, lineKey)
      next.has(k) ? next.delete(k) : next.add(k)
      return next
    })
  const invitedLineKeys = (s) => selectedItems.map(lineKeyOf).filter((k) => isInvited(s.id, k))
  const suppliersWithoutLines = selectedSuppliers.filter((s) => invitedLineKeys(s).length === 0)

  const isValid =
    (selectedSuppliers.length > 0 || newSupplierDrafts.length > 0) &&
    form.title.trim() &&
    selectedItems.length > 0 &&
    suppliersWithoutLines.length === 0 &&
    cleanTerms(requirements).length > 0 &&
    form.deadline &&
    new Date(form.deadline) > new Date() &&
    deliveryFeeValid

  const handleSubmit = async () => {
    if (!isValid || isSubmitting) return
    await onSubmit({
      supplierIds: selectedSuppliers.map((s) => s.id),
      supplierObjects: selectedSuppliers,
      newSuppliers: newSupplierDrafts,
      title: form.title.trim(),
      lineKeys: selectedItems.map(lineKeyOf),
      items: selectedItems,
      supplierLineKeys: selectedSuppliers
        .map((s) => ({ supplierId: s.id, lineKeys: invitedLineKeys(s) }))
        .filter((x) => x.lineKeys.length < selectedItems.length),
      requirements: cleanTerms(requirements),
      deliveryLocation: form.deliveryLocation.trim(),
      expectedDeliveryDate: form.expectedDeliveryDate || undefined,
      deadline: new Date(form.deadline).toISOString(),
      note: form.note.trim(),
      requestedDocuments: requestedDocs,
      contact,
      requirePin: form.requirePin,
      deliveryFee: form.deliveryFee === "" ? undefined : Number(form.deliveryFee),
    })
    // Reset
    setSelectedSuppliers([])
    setNewSupplierDrafts([])
    setExcludedPairs(new Set())
    setForm({ title: defaultTitle, deadline: addDays(7), note: "", deliveryLocation: defaultDeliveryLocation, expectedDeliveryDate: "", requirePin: true, deliveryFee: "" })
    setRequirements(requirementRows(initialRequirementsKey))
    setRequestedDocs(SUGGESTED_DOCS.slice(0, 2).map((name) => ({ name, required: true })))
    setShowNewSupplier(false)
    setNewSupplierDraft({ name: "", taxCode: "", email: "" })
  }

  if (!open) return null

  return (
    <>
      {/* Overlay */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, background: "rgba(15,23,42,.4)", zIndex: 1200,
        }}
      />
      {/* Drawer */}
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(640px, 100vw)",
          background: "var(--pc-surface)",
          borderLeft: "1px solid var(--pc-border)",
          zIndex: 1201,
          display: "flex",
          flexDirection: "column",
          boxShadow: "-8px 0 32px rgba(15,23,42,.14)",
          overflowY: "auto",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "18px 24px",
            borderBottom: "1px solid var(--pc-border)",
            background: "var(--pc-surface)",
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}
        >
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: "var(--pc-ink)" }}>
              Tạo Proposal Request
            </div>
            <div style={{ fontSize: 13, color: "var(--pc-muted)", marginTop: 2 }}>
              Gửi yêu cầu báo giá đến nhiều nhà cung cấp
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{ background: "none", border: "none", cursor: "pointer", color: "var(--pc-muted)", padding: 4 }}
            aria-label="Đóng"
          >
            <X size={22} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, padding: "20px 24px", display: "flex", flexDirection: "column", gap: 20 }}>

          {/* Vendors */}
          <div className="pc-field">
            <label className="pc-label">
              Nhà cung cấp <span className="pc-req">*</span>
            </label>
            {(selectedSuppliers.length > 0 || newSupplierDrafts.length > 0) && (
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 8 }}>
                {selectedSuppliers.map((s) => (
                  <SupplierChip key={s.id} supplier={s} onRemove={handleRemoveSupplier} />
                ))}
                {newSupplierDrafts.map((d, i) => (
                  <SupplierChip
                    key={`new-${i}`}
                    supplier={{ id: `new-${i}`, name: `${d.name} (mới)` }}
                    onRemove={() => handleRemoveNewSupplierDraft(i)}
                  />
                ))}
              </div>
            )}
            <SupplierSearch
              suppliers={suppliers}
              selected={selectedSuppliers}
              activeIds={existingActiveSupplierIds}
              onSelect={handleSelectSupplier}
            />
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<Plus size={14} aria-hidden />}
              onClick={() => setShowNewSupplier((v) => !v)}
              style={{ marginTop: 8 }}
            >
              Tạo nhà cung cấp mới
            </Button>
            {showNewSupplier && (
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8 }}>
                <Input
                  label="Tên nhà cung cấp"
                  required
                  value={newSupplierDraft.name}
                  error={newSupplierErrors.name}
                  onChange={(e) => setNewSupplierField("name", e.target.value)}
                />
                <Input
                  label="Mã số thuế"
                  required
                  value={newSupplierDraft.taxCode}
                  error={newSupplierErrors.taxCode}
                  onChange={(e) => setNewSupplierField("taxCode", e.target.value)}
                />
                <Input
                  label="Email"
                  type="email"
                  value={newSupplierDraft.email}
                  error={newSupplierErrors.email}
                  onChange={(e) => setNewSupplierField("email", e.target.value)}
                />
                <Button
                  variant="primary"
                  size="sm"
                  onClick={handleQueueNewSupplier}
                  disabled={!newSupplierDraft.name.trim() || !newSupplierDraft.taxCode.trim()}
                >
                  Thêm vào danh sách
                </Button>
              </div>
            )}
          </div>

          {/* Title */}
          <Input
            label="Tiêu đề yêu cầu"
            required
            value={form.title}
            onChange={(e) => setField("title", e.target.value)}
            placeholder="VD: Yêu cầu báo giá Laptop Dell XPS 13"
          />

          {/* Items */}
          <div className="pc-field">
            <label className="pc-label">
              Hạng mục yêu cầu báo giá <span className="pc-req">*</span>
            </label>
            {orderItems.length === 0 ? (
              <div style={{ fontSize: 13, color: "var(--pc-muted)" }}>
                {itemsLoading ? "Đang tải hạng mục…" : "Chưa có hạng mục — thêm dòng order trước."}
              </div>
            ) : (
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {orderItems.map((it) => {
                  const id = lineKeyOf(it)
                  return (
                    <label
                      key={id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 10,
                        padding: "10px 12px",
                        border: "1px solid var(--pc-border)",
                        borderRadius: "var(--pc-r-md)",
                        background: selectedItemIds.has(id) ? "var(--pc-brand-soft)" : "var(--pc-surface-2)",
                        cursor: "pointer",
                        fontSize: 13.5,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={selectedItemIds.has(id)}
                        onChange={() => toggleItem(id)}
                        style={{ accentColor: "var(--pc-brand)" }}
                      />
                      <span style={{ flex: 1, color: "var(--pc-ink)", fontWeight: 500 }}>
                        {it.itemName || it.name}
                        {it.offCatalog && (
                          <span style={{ color: "var(--pc-muted)", fontSize: 12, fontWeight: 400 }}> · ngoài catalog</span>
                        )}
                      </span>
                      <span style={{ color: "var(--pc-muted)", fontSize: 12 }}>
                        {it.quantity} {it.unit}
                      </span>
                    </label>
                  )
                })}
              </div>
            )}
          </div>

          {showLineMatrix && (
            <div className="pc-field">
              <label className="pc-label">Nhà cung cấp báo giá hạng mục nào</label>
              <div style={{ overflowX: "auto", border: "1px solid var(--pc-border)", borderRadius: "var(--pc-r-md)" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                  <thead>
                    <tr>
                      <th style={matrixHead}>Hạng mục</th>
                      {selectedSuppliers.map((s) => (
                        <th key={s.id} style={{ ...matrixHead, textAlign: "center" }}>{s.name}</th>
                      ))}
                      {newSupplierDrafts.map((d, i) => (
                        <th key={`new-${i}`} style={{ ...matrixHead, textAlign: "center" }}>{d.name} (mới)</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {selectedItems.map((it) => {
                      const k = lineKeyOf(it)
                      return (
                        <tr key={k}>
                          <td style={matrixCell}>{it.itemName || it.name}</td>
                          {selectedSuppliers.map((s) => (
                            <td key={s.id} style={{ ...matrixCell, textAlign: "center" }}>
                              <input
                                type="checkbox"
                                checked={isInvited(s.id, k)}
                                onChange={() => togglePair(s.id, k)}
                                style={{ accentColor: "var(--pc-brand)" }}
                                aria-label={`${s.name} báo giá ${it.itemName || it.name}`}
                              />
                            </td>
                          ))}
                          {newSupplierDrafts.map((d, i) => (
                            <td key={`new-${i}`} style={{ ...matrixCell, textAlign: "center" }}>
                              <input type="checkbox" checked disabled />
                            </td>
                          ))}
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
              {newSupplierDrafts.length > 0 && (
                <div style={{ fontSize: 12, color: "var(--pc-muted)", marginTop: 6 }}>
                  NCC mới nhận tất cả hạng mục.
                </div>
              )}
              {suppliersWithoutLines.length > 0 && (
                <div style={{ fontSize: 12, color: "var(--pc-danger, #b42318)", marginTop: 6 }}>
                  {suppliersWithoutLines.map((s) => s.name).join(", ")} chưa được chọn hạng mục nào.
                </div>
              )}
            </div>
          )}

          {/* Requirements */}
          <div className="pc-field">
            <label className="pc-label">
              Yêu cầu kỹ thuật / điều khoản <span className="pc-req">*</span>
            </label>
            {requirementsLocked && (
              <div style={{ fontSize: 12.5, color: "var(--pc-muted)", marginBottom: 8 }}>{LOCKED_REASON}</div>
            )}
            <RequirementInputs value={requirements} onChange={setRequirements} readOnly={requirementsLocked} />
          </div>

          {/* Deadline */}
          <div className="pc-field">
            <label className="pc-label">
              Deadline nộp báo giá <span className="pc-req">*</span>
            </label>
            <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
              {QUICK_DEADLINES.map((q) => (
                <button
                  key={q.label}
                  type="button"
                  className="pc-btn pc-btn--secondary pc-btn--sm"
                  onClick={() => setField("deadline", addDays(q.days))}
                >
                  <Clock size={12} /> {q.label}
                </button>
              ))}
            </div>
            <input
              type="datetime-local"
              className="pc-control"
              style={{ marginTop: 8 }}
              value={form.deadline}
              min={new Date().toISOString().slice(0, 16)}
              onChange={(e) => setField("deadline", e.target.value)}
            />
            {form.deadline && new Date(form.deadline) <= new Date() && (
              <span style={{ fontSize: 12, color: "var(--pc-danger)" }}>Deadline phải lớn hơn hiện tại</span>
            )}
          </div>

          {/* Delivery */}
          <Input
            label="Địa điểm giao hàng"
            value={form.deliveryLocation}
            onChange={(e) => setField("deliveryLocation", e.target.value)}
            placeholder="Tầng 3, Tòa nhà A, 123 Nguyễn Văn Linh, TP.HCM"
          />
          <Input
            label="Ngày giao hàng dự kiến"
            type="date"
            value={form.expectedDeliveryDate}
            min={todayStr()}
            onChange={(e) => setField("expectedDeliveryDate", e.target.value)}
          />
          <NumberInput
            label="Phí giao hàng (VND)"
            value={form.deliveryFee}
            onChange={(v) => setField("deliveryFee", v)}
            hint={deliveryFeeValid ? "Tuỳ chọn. Tính riêng, không cộng vào tổng PO." : "Phí giao hàng không được âm."}
          />

          {/* Note */}
          <Textarea
            label="Ghi chú cho nhà cung cấp"
            placeholder="Thông tin thêm, hướng dẫn, yêu cầu đặc biệt… (tối đa 2000 ký tự)"
            value={form.note}
            rows={3}
            maxLength={2000}
            onChange={(e) => setField("note", e.target.value)}
          />

          {/* Requested documents */}
          <div className="pc-field">
            <label className="pc-label">Tài liệu yêu cầu đính kèm</label>
            <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
              {requestedDocs.map((d, i) => (
                <span
                  key={i}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 5,
                    padding: "5px 10px",
                    background: d.required ? "var(--pc-brand-soft)" : "var(--pc-surface-2)",
                    border: `1px solid ${d.required ? "#c7d2fe" : "var(--pc-border)"}`,
                    borderRadius: "var(--pc-r-pill)",
                    fontSize: 12.5,
                    cursor: "pointer",
                    fontWeight: 500,
                    color: d.required ? "var(--pc-brand)" : "var(--pc-body)",
                  }}
                  onClick={() => toggleDocRequired(i)}
                  title={d.required ? "Click để bỏ bắt buộc" : "Click để đánh dấu bắt buộc"}
                >
                  {d.required && <span style={{ fontSize: 10 }}>★</span>}
                  {d.name}
                  <button
                    type="button"
                    onClick={(e) => { e.stopPropagation(); removeDoc(i) }}
                    style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: "inherit", lineHeight: 1 }}
                    aria-label="Xóa"
                  >
                    <X size={11} />
                  </button>
                </span>
              ))}
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
              <input
                className="pc-control"
                style={{ flex: 1 }}
                placeholder="Tên tài liệu mới…"
                value={newDocName}
                onChange={(e) => setNewDocName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addDoc()}
              />
              <Button variant="secondary" size="sm" onClick={addDoc} disabled={!newDocName.trim()}>
                <Plus size={14} /> Thêm
              </Button>
            </div>
            <span className="pc-hint">Click vào chip để bật/tắt bắt buộc (★). Vendor sẽ thấy danh sách này.</span>
          </div>

          {/* Contact person */}
          <div className="pc-field">
            <label className="pc-label">Người liên hệ</label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10 }}>
              <input
                className="pc-control"
                placeholder="Tên"
                value={contact.name}
                onChange={(e) => setContact((p) => ({ ...p, name: e.target.value }))}
              />
              <input
                className="pc-control"
                placeholder="Email"
                type="email"
                value={contact.email}
                onChange={(e) => setContact((p) => ({ ...p, email: e.target.value }))}
              />
              <input
                className="pc-control"
                placeholder="Điện thoại"
                value={contact.phone}
                onChange={(e) => setContact((p) => ({ ...p, phone: e.target.value }))}
              />
            </div>
          </div>

          {/* Require PIN */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "14px 16px",
              border: "1px solid var(--pc-border)",
              borderRadius: "var(--pc-r-md)",
              background: "var(--pc-surface-2)",
            }}
          >
            <div>
              <div style={{ fontSize: 14, fontWeight: 600, color: "var(--pc-ink)" }}>
                Yêu cầu mã PIN
              </div>
              <div style={{ fontSize: 12, color: "var(--pc-muted)", marginTop: 2 }}>
                Mỗi vendor sẽ nhận một mã PIN 6 số riêng để mở link
              </div>
            </div>
            <button
              type="button"
              role="switch"
              aria-checked={form.requirePin}
              onClick={() => setField("requirePin", !form.requirePin)}
              style={{
                width: 44,
                height: 24,
                borderRadius: 12,
                border: "none",
                cursor: "pointer",
                background: form.requirePin ? "var(--pc-brand)" : "var(--pc-border-strong)",
                position: "relative",
                transition: "background .2s",
                flex: "none",
              }}
            >
              <span
                style={{
                  position: "absolute",
                  top: 3,
                  left: form.requirePin ? 23 : 3,
                  width: 18,
                  height: 18,
                  borderRadius: "50%",
                  background: "#fff",
                  transition: "left .2s",
                  boxShadow: "0 1px 4px rgba(0,0,0,.2)",
                }}
              />
            </button>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: "16px 24px",
            borderTop: "1px solid var(--pc-border)",
            background: "var(--pc-surface)",
            display: "flex",
            gap: 10,
            justifyContent: "flex-end",
            position: "sticky",
            bottom: 0,
          }}
        >
          <Button variant="secondary" onClick={onClose} disabled={isSubmitting}>
            Hủy
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!isValid || isSubmitting}
          >
            {isSubmitting
              ? "Đang tạo…"
              : (() => {
                  const total = selectedSuppliers.length + newSupplierDrafts.length
                  return `Tạo yêu cầu (${total} vendor${total !== 1 ? "s" : ""})`
                })()}
          </Button>
        </div>
      </div>
    </>
  )
}
